import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useEffect, useMemo, useState } from 'react';
import { IconButton } from '@/components/ui/button';
import { lowerFDI, upperFDI, FDI_Notation } from '@common/FDI_Notation.ts';
import useSegmentationStore from '@segmentation/hooks/useSegmentationStore.ts';
import { threeInteractionStore } from '@modules/three/store/threeInteractionStore.ts';
import { SegmentationPhase } from '@segmentation/store/segmentationStore.ts';
import SelectionLimits from './SelectionLimits';

interface SelectionUIProps {
  setIsSelected: (isSegmented: boolean) => void;
  jaw: 'upper' | 'lower';
}

export const SelectionUI = ({ setIsSelected, jaw }: SelectionUIProps) => {
  useDocumentTitle(`Braces Designer - Teeth Segmentation | fullName`);
  const { jaws, update, segmentationPhase } = useSegmentationStore();
  const isLowerPhase = segmentationPhase === SegmentationPhase.TEETH_SELECTION_LOWER;
  const baseFDI = isLowerPhase ? lowerFDI : upperFDI;
  const [missingCount, setMissingCount] = useState(0);
  // Keep a simple history of progression to know if last step was a missing tooth or an anchor
  const [history, setHistory] = useState<Array<'anchor' | 'missing'>>([]);
  // Subscribe to anchor changes to force re-render when user adds/removes points in 3D
  const anchorTick = threeInteractionStore((s) => s.anchorTick);
  // Always resolve the active jaw from the current segmentation phase to avoid mismatches
  const actualJaw = isLowerPhase ? jaws.lowerJaw : jaws.upperJaw;
  const selectionPoints = actualJaw.getMeshAnchorCollection();
  const selectionIndex = useMemo(() => selectionPoints.length, [anchorTick, selectionPoints]);
  const progressIndex = Math.min(selectionIndex + missingCount, baseFDI.length - 1);
  const [lockedFDI, setLockedFDI] = useState<FDI_Notation | undefined>(undefined);

  // Reset local progress when switching jaws or phase
  useEffect(() => {
    setMissingCount(0);
    setHistory([]);
  }, [jaw, segmentationPhase]);

  // Sync history with external anchor add/remove events
  useEffect(() => {
    const anchorCountInHistory = history.reduce((acc, h) => acc + (h === 'anchor' ? 1 : 0), 0);
    if (selectionIndex > anchorCountInHistory) {
      const delta = selectionIndex - anchorCountInHistory;

      const activeIndexBeforeAdd = anchorCountInHistory + missingCount;
      const safeIndex = Math.min(Math.max(0, activeIndexBeforeAdd), baseFDI.length - 1);
      const clickedFDI = baseFDI[safeIndex];
      if (clickedFDI !== undefined) {
        setLockedFDI(clickedFDI);
      }

      setHistory((h) => h.concat(new Array(delta).fill('anchor')));
    } else if (selectionIndex < anchorCountInHistory) {
      let delta = anchorCountInHistory - selectionIndex;
      setHistory((h) => {
        const out = [...h];
        while (delta > 0 && out.length) {
          const last = out[out.length - 1];
          out.pop();
          if (last === 'anchor') delta--;
        }
        return out;
      });
      // If anchors were REMOVED, clear lockedFDI (we no longer have a frozen target)
      setLockedFDI(undefined);
    }
  }, [selectionIndex]);

  const handleRemoveCurrent = () => {
    if (history.length === 0) {
      return;
    }
    const last = history[history.length - 1];
    if (last === 'missing') {
      setHistory((h) => h.slice(0, -1));
      setMissingCount((c) => Math.max(0, c - 1));
    } else {
      actualJaw.removeLastAnchor();
      update();
      // if we removed an anchor, unlock the frozen FDI so SelectionLimits can react again
      setLockedFDI(undefined);
    }
  };

  const handleMissingTooth = () => {
    if (progressIndex === baseFDI.length - 1) {
      return;
    }
    setHistory((h) => h.concat('missing'));
    setMissingCount((c) => c + 1);
  };

  const handleRemoveAll = () => {
    setHistory([]);
    setMissingCount(0);
    actualJaw.clearAnchors();
    update();
    // clear the lock when everything is removed
    setLockedFDI(undefined);
  };

  const handleDone = () => {
    // Build mapping: which anchors are followed by at least one missing tooth
    const missingAfterSet = new Set<number>();
    let lastAnchorIdx = -1;
    let afterMissing = false;
    let anchorCounter = -1;
    for (const step of history) {
      if (step === 'anchor') {
        // Close previous anchor block
        if (lastAnchorIdx >= 0 && afterMissing) {
          missingAfterSet.add(lastAnchorIdx);
        }
        anchorCounter += 1;
        lastAnchorIdx = anchorCounter;
        afterMissing = false;
      } else {
        // step === 'missing'
        if (lastAnchorIdx >= 0) afterMissing = true;
      }
    }
    // No need to commit final afterMissing unless you want to mark the last anchor
    // (there is no next anchor to connect to)
    actualJaw.setMissingAfter(missingAfterSet);

    if (segmentationPhase === SegmentationPhase.TEETH_SELECTION_LOWER) {
      jaws.lowerJaw.finishAnchorInsertion();
      setIsSelected(true);
      threeInteractionStore.getState().setPointAdding(false);
    } else {
      jaws.upperJaw.finishAnchorInsertion();
      setIsSelected(true);
      threeInteractionStore.getState().setPointAdding(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <section className="rounded-3xl border border-primary/40 bg-content/80 p-6 text-white shadow-xl shadow-primary/20 backdrop-blur">
        <header className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Teeth segmentation</p>
              <h2 className="text-2xl font-semibold tracking-tight text-white">Mark tooth positions</h2>
            </div>

            {(() => {
              const currentFDI = baseFDI[progressIndex];
              const isDone = currentFDI === 48 || currentFDI === 28;

              return (
                <span
                  className={`rounded-2xl border px-4 py-2 text-2xl font-semibold tracking-tight text-white ${
                    isDone
                      ? 'border-green-500 bg-green-800/30 '
                      : 'border-primary/60 bg-primary/20'
                  }`}
                >
                  {isDone ? 'OK' : currentFDI}
                </span>
              );
            })()}

          </div>
          <p className="rounded-2xl border border-content-highlight/50 bg-content-dark/60 px-4 py-4 text-sm leading-relaxed text-white/80">
            Select the tooth by clicking on the surface. Use the buttons below to correct mistakes or mark missing teeth.
          </p>
        </header>

        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <IconButton
              onClick={handleRemoveCurrent}
              variant="ghost-light"
              className="h-12 rounded-2xl border border-content-highlight/40 bg-content-dark/80 text-sm font-semibold uppercase tracking-[0.25em]"
            >
              Remove
            </IconButton>

            <IconButton
              onClick={handleMissingTooth}
              variant="ghost-danger-glow"
              className="h-12 rounded-2xl bg-red-900/90 text-sm font-semibold uppercase tracking-[0.25em] text-white"
            >
              Missing
            </IconButton>
          </div>

          <IconButton
            onClick={handleRemoveAll}
            variant="ghost-danger"
            className="h-12 w-full rounded-2xl bg-red-900/90 text-sm font-semibold uppercase tracking-[0.25em]"
          >
            Remove all
          </IconButton>
        </div>

        <div className="mt-6 flex justify-center">
          <IconButton
            onClick={handleDone}
            variant="ghost-primary"
            size="lg"
            className="w-full rounded-2xl border border-primary/50 bg-primary/80 text-sm font-semibold uppercase tracking-[0.3em] hover:bg-primary"
          >
            Confirm selection
          </IconButton>
        </div>
      </section>

      <SelectionLimits selectedFDI={lockedFDI ?? baseFDI[progressIndex]} />
    </div>
  );
};
